/**
 * Captions Clip Interfaces
 * Props and configuration interfaces for CaptionsClip
 */

import type { ClipProps } from '../clip';
import type { VisualMixinProps } from '../mixins';
import type { 
    CaptionsDisplayMode,
    CaptionsTextAlign,
    CaptionsTextBaseline,
    CaptionsTextCase,
    CaptionsTextStyle,
    CaptionsHighlightStyle,
    CaptionsBackground,
    CaptionsPhraseConfig,
    CaptionWord,
} from './captions.types';

/**
 * Props for creating/configuring a CaptionsClip
 */
export interface CaptionsClipProps extends ClipProps, Omit<VisualMixinProps, 'anchor'> {
    /**
     * Reference to the ScriptLayerClip ID this captions clip is tied to.
     * The captions will source their words from this script clip.
     */
    scriptClipId?: string;

    /**
     * Temporal scope for this captions clip:
     * - "all": spans the full ScriptClip (composition-wide captions)
     * - "scene": spans only the scene it was created for
     *
     * Used to keep captions timing stable across script edits (cuts/insertions/undo).
     * @default 'all'
     */
    timingScope?: 'all' | 'scene';

    /**
     * Display mode for captions
     * @default 'phrase'
     */
    displayMode?: CaptionsDisplayMode;

    /**
     * Text alignment within the caption area
     * @default 'center'
     */
    textAlign?: CaptionsTextAlign;

    /**
     * Text baseline alignment
     * @default 'middle'
     */
    textBaseline?: CaptionsTextBaseline;

    /**
     * Text case transformation
     * @default 'none'
     */
    textCase?: CaptionsTextCase;

    /**
     * Base text style configuration
     */
    textStyle?: Partial<CaptionsTextStyle>;

    /**
     * Highlight style for current word (in 'phrase' mode)
     */
    highlightStyle?: Partial<CaptionsHighlightStyle>;

    /**
     * Background box configuration
     */
    background?: Partial<CaptionsBackground>;

    /**
     * Phrase grouping configuration (for 'phrase' mode)
     */
    phraseConfig?: Partial<CaptionsPhraseConfig>;

    /**
     * Maximum width for text wrapping (in pixels or percentage)
     * @default '80%' of composition width
     */
    maxWidth?: number | string;

    /**
     * Words to display - can be provided directly or sourced from scriptClip
     * If not provided, will attempt to get from script clip
     */
    words?: CaptionWord[];
}

/**
 * Default text style values
 */
export const DEFAULT_TEXT_STYLE: CaptionsTextStyle = {
    fontFamily: 'Arial',
    fontSize: 48,
    fontWeight: 'bold',
    fontStyle: 'normal',
    fillColor: '#FFFFFF',
    stroke: {
        color: '#000000',
        width: 4,
        alpha: 1,
        join: 'round',
    },
    shadow: {
        color: '#000000',
        blur: 4,
        distance: 2,
        angle: Math.PI / 4,
        alpha: 0.5,
    },
};

/**
 * Default highlight style values
 */
export const DEFAULT_HIGHLIGHT_STYLE: CaptionsHighlightStyle = {
    color: '#00BFFF', // DeepSkyBlue - visible against white text
    scale: 1,
};

/**
 * Default background style values
 */
export const DEFAULT_BACKGROUND: CaptionsBackground = {
    fill: '#000000',
    alpha: 0, // Transparent by default
    borderRadius: 8,
    padding: { x: 20, y: 10 },
};

/**
 * Default phrase configuration
 */
export const DEFAULT_PHRASE_CONFIG: CaptionsPhraseConfig = {
    maxWordsPerPhrase: 8,
    maxPhraseDuration: 3,
    maxPhraseLength: 50,
};
